import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, theme, Button, Dropdown, message } from "antd";
import {
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  StarOutlined,
  LogoutOutlined,
  SettingOutlined,
  PlusOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import Dashboard from "./pages/Dashboard";
import StaffManagement from "./pages/StaffManagement";
import ElderVisitRelationManagement from "./pages/ElderVisitRelationManagement";
import ValueAddedServiceManagement from "./pages/ValueAddedServiceManagement";
import PendingReports from "./pages/PendingReports";
import PublishedReports from "./pages/PublishedReports";
import ServiceEvaluation from "./pages/ServiceEvaluation";
import CreateCustomer from "./pages/CreateCustomer";
import SchedulerTrigger from "./pages/SchedulerTrigger";
import Login from "./pages/Login";
import WechatCallback from "./pages/WechatCallback";
import { authService, type AdminUser } from "./services/auth.service";
import logoPng from "./static/logo.png";

const { Header, Content, Sider } = Layout;

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
}

const App: React.FC = () => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [currentAdmin, setCurrentAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  // 初始化认证状态
  useEffect(() => {
    const initAuth = async () => {
      try {
        const authenticated = authService.isAuthenticated();
        if (authenticated) {
          // 验证token有效性
          const isValid = await authService.validateToken();
          if (isValid) {
            setIsAuthenticated(true);
            setCurrentAdmin(authService.getCurrentAdmin());
          } else {
            authService.logout();
            setIsAuthenticated(false);
            setCurrentAdmin(null);
          }
        }
      } catch (error) {
        console.error("认证初始化失败:", error);
        authService.logout();
        setIsAuthenticated(false);
        setCurrentAdmin(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // 处理登录成功
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setCurrentAdmin(authService.getCurrentAdmin());
  };

  // 处理登出
  const handleLogout = () => {
    try {
      // 使用 setTimeout 来避免 ResizeObserver 错误
      setTimeout(() => {
        authService.logout();
        setIsAuthenticated(false);
        setCurrentAdmin(null);
        navigate("/login");
      }, 0);
    } catch (error) {
      console.error("登出过程中发生错误:", error);
      // 即使出错也要清除状态
      authService.logout();
      setIsAuthenticated(false);
      setCurrentAdmin(null);
      navigate("/login");
    }
  };

  const menuItems: MenuItem[] = [
    {
      key: "/",
      icon: <DashboardOutlined />,
      label: "运营看板（待开发）",
    },
    {
      key: "/staff",
      icon: <TeamOutlined />,
      label: "服务人员管理",
    },
    {
      key: "/elder-staff-relations",
      icon: <UserOutlined />,
      label: "老人随访分配",
    },
    {
      key: "/value-added-services",
      icon: <SettingOutlined />,
      label: "增值服务分配",
    },
    {
      key: "/pending-reports",
      icon: <FileTextOutlined />,
      label: "待发布报告列表",
    },
    {
      key: "/published-reports",
      icon: <FileDoneOutlined />,
      label: "已发布报告列表",
    },
    {
      key: "/create-customer",
      icon: <PlusOutlined />,
      label: "后台创建客户",
    },
    {
      key: "/scheduler-trigger",
      icon: <PlayCircleOutlined />,
      label: "触发随访安排",
    },
    {
      key: "/service-evaluation",
      icon: <StarOutlined />,
      label: "服务评价管理(待开发)",
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  // 根据当前路由获取页面标题
  const getCurrentPageTitle = () => {
    const currentItem = menuItems.find(
      (item) => item.key === location.pathname
    );
    return currentItem ? currentItem.label : "运营看板";
  };

  // 用户菜单
  const userMenuItems = [
    {
      key: "profile",
      icon: <SettingOutlined />,
      label: "个人设置",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === "logout") {
      handleLogout();
    } else if (key === "profile") {
      // TODO: 实现个人设置页面
      message.info("功能待开发，敬请期待");
    }
  };

  // 加载状态
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          fontSize: "16px",
        }}
      >
        正在加载...
      </div>
    );
  }

  // 未登录状态，显示登录页面或回调页面
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/login"
          element={<Login onLoginSuccess={handleLoginSuccess} />}
        />
        <Route
          path="/wechat-callback"
          element={<WechatCallback onLoginSuccess={handleLoginSuccess} />}
        />
        <Route
          path="*"
          element={<Login onLoginSuccess={handleLoginSuccess} />}
        />
      </Routes>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed}>
        <div
          className="logo"
          style={{
            height: 32,
            margin: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            color: "#fff",
            fontSize: "16px",
            fontWeight: "bold",
            transition: "all 0.2s",
          }}
        >
          <img
            src={logoPng}
            alt="安安LOGO"
            style={{
              width: 24,
              height: 24,
              marginRight: collapsed ? 0 : 8,
              transition: "all 0.2s",
            }}
          />
          {!collapsed && <span>安安养老</span>}
        </div>
        <Menu
          theme="dark"
          selectedKeys={[location.pathname]}
          mode="inline"
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: "0 24px",
            background: colorBgContainer,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 1px 4px rgba(0,21,41,0.08)",
          }}
        >
          <div
            style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#1890ff",
              letterSpacing: "0.5px",
              background: "linear-gradient(135deg, #1890ff 0%, #722ed1 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            运营管理后台 - {getCurrentPageTitle()}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#666",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <Button
                type="text"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "4px 8px",
                  height: "auto",
                }}
              >
                {/* 管理员头像 */}
                {currentAdmin?.avatar ? (
                  <img
                    src={currentAdmin.avatar}
                    alt="管理员头像"
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#1890ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {currentAdmin?.name?.charAt(0) ||
                      currentAdmin?.unionid?.charAt(0) ||
                      "?"}
                  </span>
                )}
                <span>
                  {currentAdmin?.name ||
                    `管理员 (${currentAdmin?.unionid?.slice(-6) || "Unknown"})`}
                </span>
              </Button>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: "0 16px",
          }}
        >
          <div
            style={{
              padding: 24,
              minHeight: 360,
              background: colorBgContainer,
              marginTop: 16,
            }}
          >
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/create-customer" element={<CreateCustomer />} />
              <Route path="/scheduler-trigger" element={<SchedulerTrigger />} />
              <Route path="/staff" element={<StaffManagement />} />
              <Route
                path="/elder-staff-relations"
                element={<ElderVisitRelationManagement />}
              />
              <Route
                path="/value-added-services"
                element={<ValueAddedServiceManagement />}
              />
              <Route path="/pending-reports" element={<PendingReports />} />
              <Route path="/published-reports" element={<PublishedReports />} />
              <Route
                path="/service-evaluation"
                element={<ServiceEvaluation />}
              />
              <Route
                path="/wechat-callback"
                element={<WechatCallback onLoginSuccess={handleLoginSuccess} />}
              />
              <Route
                path="/login"
                element={<Login onLoginSuccess={handleLoginSuccess} />}
              />
            </Routes>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;
